import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Alert, message, Space, Form, Input, Divider } from 'antd';
import { MailOutlined, ReloadOutlined, LoadingOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';

const { Title, Text, Paragraph } = Typography;

const UnverifiedMessage = () => {
    const [loading, setLoading] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [attempts, setAttempts] = useState(0);
    const [alreadyVerified, setAlreadyVerified] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [messageApi, contextHolder] = message.useMessage();
    const [messageQueue, setMessageQueue] = useState([]);

    // Get user data from localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    // Handle message queue
    useEffect(() => {
        if (messageQueue.length > 0) {
            const { type, content } = messageQueue[0];
            messageApi[type]({
                content,
                duration: type === 'warning' ? 3 : 5
            });
            setMessageQueue(prev => prev.slice(1));
        }
    }, [messageQueue, messageApi]);

    const showMessage = (content, type = 'success') => {
        setMessageQueue(prev => [...prev, { type, content }]);
    };

    // Set initial form values
    useEffect(() => {
        if (user?.email) {
            form.setFieldsValue({
                email: user.email
            });
        }
    }, [form, user]);

    useEffect(() => {
        // Load attempts from localStorage
        const savedAttempts = localStorage.getItem('verificationAttempts');
        const lastAttemptTime = localStorage.getItem('lastVerificationAttempt');
        
        if (savedAttempts && lastAttemptTime) {
            const timeDiff = Date.now() - parseInt(lastAttemptTime);
            if (timeDiff < 24 * 60 * 60 * 1000) { // 24 hours
                setAttempts(parseInt(savedAttempts));
                if (timeDiff < 60 * 1000) { // 1 minute cooldown
                    setCountdown(60 - Math.floor(timeDiff / 1000));
                }
            } else {
                // Reset if 24 hours passed
                localStorage.removeItem('verificationAttempts');
                localStorage.removeItem('lastVerificationAttempt');
            }
        }
    }, []);

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setInterval(() => {
                setCountdown(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [countdown]);

    const validateEmail = (_, value) => {
        if (!value) {
            return Promise.reject('Vui lòng nhập email!');
        }

        value = value.trim().toLowerCase();

        // Basic email format
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
        if (!emailRegex.test(value)) {
            return Promise.reject('Email không đúng định dạng!');
        }

        // Length validation
        if (value.length > 255) {
            return Promise.reject('Email không được vượt quá 255 ký tự!');
        }

        // Local part validation
        const localPart = value.split('@')[0];
        if (localPart.length < 3) {
            return Promise.reject('Tên email quá ngắn!');
        }

        if (!/^[a-zA-Z0-9][a-zA-Z0-9._-]*[a-zA-Z0-9]$/.test(localPart)) {
            return Promise.reject('Email chỉ được chứa chữ cái, số và các ký tự . _ -');
        }

        if (/[._-]{2,}/.test(localPart)) {
            return Promise.reject('Email không được chứa các ký tự đặc biệt liên tiếp!');
        }

        return Promise.resolve();
    };

    const handleResendEmail = async (values) => {
        if (countdown > 0) {
            showMessage(`Vui lòng đợi ${countdown} giây trước khi gửi lại!`, 'warning');
            return;
        }

        if (attempts >= 5) {
            showMessage('Bạn đã vượt quá số lần gửi cho phép. Vui lòng thử lại sau 24 giờ!', 'error');
            return;
        }

        try {
            setLoading(true);
            setAlreadyVerified(false);
            setErrorMsg('');
            const response = await axios.post(
                `${config.apiUrl}/api/v1/email/resend-verification`,
                { email: values.email.trim().toLowerCase() }
            );

            if (response.data.success) {
                showMessage('Email xác thực đã được gửi. Vui lòng kiểm tra hộp thư của bạn!');
                form.resetFields();
                // remove localStorage user
                localStorage.removeItem('user');
                // Update attempts
                const newAttempts = attempts + 1;
                setAttempts(newAttempts);
                localStorage.setItem('verificationAttempts', newAttempts);
                localStorage.setItem('lastVerificationAttempt', Date.now().toString());
                
                // Set cooldown
                setCountdown(60);
            } else {
                showMessage(response.data.message || 'Không thể gửi email xác thực!', 'error');
            }
        } catch (error) {
            if (error.response?.data?.error_code === 'USER_NOT_FOUND') {
                console.log('Resend verification: email not found -', values.email);
                setErrorMsg('Email không tồn tại trên hệ thống, vui lòng kiểm tra lại!');
            } else if (error.response?.status === 429) {
                setErrorMsg('Quá nhiều yêu cầu. Vui lòng thử lại sau!');
            } else if (error.response?.data?.error_code === 'ALREADY_VERIFIED') {
                setAlreadyVerified(true);
                showMessage('Email này đã được xác thực!');
            } else {
                console.error('Resend verification error:', error);
                setErrorMsg(
                    error.response?.data?.message ||
                    'Có lỗi xảy ra khi gửi email xác thực. Vui lòng thử lại!'
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            padding: '20px'
        }}>
            {contextHolder}
            <Card style={{ 
                width: '100%', 
                maxWidth: 600,
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                borderRadius: '12px',
                textAlign: 'center'
            }}>
                <div style={{ marginBottom: 32 }}>
                    {alreadyVerified ? (
                        <CheckCircleOutlined style={{ 
                            fontSize: 64, 
                            color: '#52c41a',
                            marginBottom: 24 
                        }} />
                    ) : (
                        <MailOutlined style={{ 
                            fontSize: 64, 
                            color: '#1890ff',
                            marginBottom: 24 
                        }} />
                    )}
                    
                    <Title level={2} style={{ color: '#2c3e50' }}>
                        {alreadyVerified ? 'Email đã xác thực' : 'Xác thực email'}
                    </Title>
                    
                    <Paragraph style={{ 
                        fontSize: 16,
                        color: '#34495e',
                        marginBottom: 24
                    }}>
                        {alreadyVerified 
                            ? 'Email của bạn đã được xác thực. Bạn có thể đăng nhập để tiếp tục sử dụng dịch vụ.' 
                            : 'Để tiếp tục sử dụng dịch vụ, vui lòng xác thực địa chỉ email của bạn.'}
                    </Paragraph>
                </div>

                {alreadyVerified ? (
                    <Alert
                        message="Email đã được xác thực"
                        description="Tài khoản của bạn đã được xác thực thành công. Bạn có thể đăng nhập để sử dụng dịch vụ."
                        type="success"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />
                ) : (
                    <Alert
                        message="Hướng dẫn"
                        description={
                            <ul style={{ textAlign: 'left', paddingLeft: 20 }}>
                                <li>Kiểm tra hộp thư đến và thư rác</li>
                                <li>Đảm bảo email được nhập chính xác</li>
                                <li>Thời gian chờ giữa các lần gửi: 60 giây</li>
                                <li>Giới hạn: 5 lần gửi trong 24 giờ</li>
                            </ul>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />
                )}

                {alreadyVerified ? (
                    <Button 
                        type="primary" 
                        size="large"
                        block
                        onClick={() => navigate('/login')}
                        style={{ height: '48px' }}
                    >
                        Đăng nhập ngay
                    </Button>
                ) : (
                    <Form
                        form={form}
                        onFinish={handleResendEmail}
                        layout="vertical"
                        style={{ textAlign: 'left' }}
                    >
                        {errorMsg && (
                            <Alert
                                message={errorMsg}
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}
                        <Form.Item
                            name="email"
                            rules={[{ validator: validateEmail }]}                        
                        >
                            <Input 
                                prefix={<MailOutlined className="site-form-item-icon" />}
                                placeholder="Nhập email của bạn"
                                size="large"
                                maxLength={255}
                                disabled={loading || countdown > 0}
                                autoComplete="username"
                            />
                        </Form.Item>

                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Button 
                                type="primary" 
                                icon={loading ? <LoadingOutlined /> : <ReloadOutlined />}
                                loading={loading}
                                htmlType="submit"
                                size="large"
                                block
                                disabled={countdown > 0}
                                style={{ height: '48px' }}
                            >
                                {countdown > 0 
                                    ? `Vui lòng đợi ${countdown}s` 
                                    : 'Gửi lại email xác thực'}
                            </Button>

                            {attempts > 0 && (
                                <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
                                    Số lần đã gửi: {attempts}/5
                                </Text>
                            )}

                            <Divider />

                            <Button 
                                type="link" 
                                onClick={() => navigate('/login')}
                                block
                            >
                                Quay lại trang đăng nhập
                            </Button>
                        </Space>
                    </Form>
                )}
            </Card>
        </div>
    );
};

export default UnverifiedMessage;